import { useState, useEffect } from "react";
import { queryKeys } from "@/lib/queryKeys";
import { formatPrice } from "@/lib/numberFormat";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

interface CreateVoucherDialogProps {
  open: boolean;
  onClose: () => void;
  voucher?: any;
}

export function CreateVoucherDialog({ open, onClose, voucher }: CreateVoucherDialogProps) {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    discountType: 'percentage',
    discountValue: 0,
    subscriptionPlanId: '',
    subscriptionDuration: 30,
    maxUses: '',
    usesPerUser: 1,
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: '',
    isActive: true,
    autoGenerateCode: true
  });

  
  const queryClient = useQueryClient();

  // Fetch subscription plans
  const { data: plans } = useQuery<any[]>({
    queryKey: queryKeys.subscriptions.plans(),
    queryFn: async () => {
      // apiRequest already returns parsed JSON — no .json() call needed
      return apiRequest('GET', '/api/admin/subscription-plans') as unknown as Promise<any[]>;
    }
  });

  // Populate form when editing — re-runs on open so re-opening the same voucher
  // after a cancelled edit always shows the server's current values, not dirty state
  useEffect(() => {
    if (open && voucher) {
      setFormData({
        code: voucher.code,
        name: voucher.name,
        description: voucher.description || '',
        discountType: voucher.discountType,
        discountValue: voucher.discountValue || 0,
        subscriptionPlanId: voucher.subscriptionPlanId || '',
        subscriptionDuration: voucher.subscriptionDuration || 30,
        maxUses: voucher.maxUses?.toString() || '',
        usesPerUser: voucher.usesPerUser ?? 1,
        validFrom: new Date(voucher.validFrom).toISOString().split('T')[0],
        validUntil: new Date(voucher.validUntil).toISOString().split('T')[0],
        isActive: voucher.isActive,
        autoGenerateCode: false
      });
    }
  }, [voucher, open]);

  // Reset form to defaults when dialog opens for creating a new voucher
  useEffect(() => {
    if (open && !voucher) {
      setFormData({
        code: '',
        name: '',
        description: '',
        discountType: 'percentage',
        discountValue: 0,
        subscriptionPlanId: '',
        subscriptionDuration: 30,
        maxUses: '',
        usesPerUser: 1,
        validFrom: new Date().toISOString().split('T')[0],
        validUntil: '',
        isActive: true,
        autoGenerateCode: true
      });
    }
  }, [open, voucher]);

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const { fetchAdminWorkspaceId } = await import('@/lib/adminWorkspaceHelper');
      const adminWorkspaceId = await fetchAdminWorkspaceId();
      if (!adminWorkspaceId) throw new Error('Admin workspace not found');

      const url = voucher ? `/api/vouchers/${voucher._id}` : '/api/vouchers';
      const method = voucher ? 'PATCH' : 'POST';
      return apiRequest(method, url, data, { 'x-workspace-id': adminWorkspaceId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.all() });
      showSuccess(voucher ? 'Voucher updated' : 'Voucher created');
      onClose();
    },
    onError: (error: any) => {
      showError(error?.message || 'Failed to save voucher');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.validFrom && formData.validUntil && formData.validUntil <= formData.validFrom) {
      showError('"Valid Until" must be after "Valid From".');
      return;
    }

    if (formData.discountType === 'subscription' && !formData.subscriptionPlanId) {
      showError('Please select a subscription plan.');
      return;
    }

    const data: any = {
      ...formData,
      // parseInt on an empty string returns NaN — guard with || null / || default
      maxUses: formData.maxUses ? parseInt(formData.maxUses) || null : null,
      usesPerUser: parseInt(String(formData.usesPerUser)) || 1,
      subscriptionDuration: parseInt(String(formData.subscriptionDuration)) || 30,
    };

    if (formData.autoGenerateCode && !voucher) {
      delete data.code;
    }
    delete data.autoGenerateCode;

    mutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{voucher ? 'Edit' : 'Create'} Voucher</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Voucher Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="code">Voucher Code</Label>
              <div className="flex gap-2">
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  disabled={formData.autoGenerateCode && !voucher}
                  placeholder={formData.autoGenerateCode ? "Auto-generated" : "ENTER CODE"}
                />
                {!voucher && (
                  <Switch
                    checked={formData.autoGenerateCode}
                    onCheckedChange={(checked) => setFormData({ ...formData, autoGenerateCode: checked })}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="discountType">Discount Type *</Label>
              <Select
                value={formData.discountType}
                onValueChange={(value) => setFormData({ ...formData, discountType: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="fixed">Fixed Amount</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.discountType !== 'subscription' ? (
              <div className="space-y-2">
                <Label htmlFor="discountValue">
                  {formData.discountType === 'percentage' ? 'Percentage (%)' : 'Amount (₱)'}
                </Label>
                <Input
                  id="discountValue"
                  type="number"
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="subscriptionPlanId">Subscription Plan *</Label>
                  <Select
                    value={formData.subscriptionPlanId}
                    onValueChange={(value) => setFormData({ ...formData, subscriptionPlanId: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {plans?.map((plan: any) => (
                        <SelectItem key={plan._id} value={plan._id}>
                          {plan.name} - ₱{formatPrice(plan.priceMonthly)}/mo {plan.priceYearly ? `/ ₱${formatPrice(plan.priceYearly)}/yr` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    User will get free access to this plan
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="subscriptionDuration">Duration (days)</Label>
                  <Input
                    id="subscriptionDuration"
                    type="number"
                    value={formData.subscriptionDuration}
                    onChange={(e) => setFormData({ ...formData, subscriptionDuration: parseInt(e.target.value) })}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    How long the free subscription will last
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="maxUses">Max Total Uses (leave empty for unlimited)</Label>
              <Input
                id="maxUses"
                type="number"
                value={formData.maxUses}
                onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                placeholder="Unlimited"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="usesPerUser">Uses Per User</Label>
              <Input
                id="usesPerUser"
                type="number"
                value={formData.usesPerUser}
                onChange={(e) => setFormData({ ...formData, usesPerUser: parseInt(e.target.value) })}
                required
                min="1"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="validFrom">Valid From *</Label>
              <Input
                id="validFrom"
                type="date"
                value={formData.validFrom}
                onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="validUntil">Valid Until *</Label>
              <Input
                id="validUntil"
                type="date"
                value={formData.validUntil}
                onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
            />
            <Label htmlFor="isActive">Active</Label>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : voucher ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
